// Staff View
// Handles seller panel logic and actions

import {
    callCustomer as callCustomerService,
    startAttention,
    finishService,
    markNoShow,
    reactivateCustomer,
    removePreferentialStatus,
    getQueueCounts
} from '../services/queue-service.js';
import { getSellerInfo, startShift, endShift, togglePause } from '../services/seller-service.js';
import { isSellerBusy } from '../services/seller-service.js';
import { createCustomerCard } from '../components/customer-card.js';
import { updateQueueCounts } from '../components/filters.js';
import { STATUS, FILTER_TYPES } from '../config/constants.js';
import stateManager from '../core/state-manager.js';
import { logout } from '../core/auth-manager.js';

/**
 * Initialize staff view controls
 */
export function initStaffViewControls() {
    // Shift control
    document.getElementById('shift-btn').addEventListener('click', handleShiftToggle);

    // Pause control
    document.getElementById('pause-seller-btn').addEventListener('click', handlePauseToggle);

    // Logout
    document.getElementById('logout-btn').addEventListener('click', logout);

    // Queue action delegation
    document.getElementById('queue-list').addEventListener('click', handleQueueAction);
}

/**
 * Handle shift toggle (start/end)
 */
async function handleShiftToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isShiftActive = stateManager.getState('isShiftActive');

    if (!currentUser) return;

    if (isShiftActive) {
        // End shift and logout
        await endShift(currentUser.uid);
        await logout();
    } else {
        // Start shift
        await startShift(currentUser.uid);
        stateManager.setShiftActive(true);
        updateShiftAndPauseButtonsState();

        // Refresh view
        const allCustomers = stateManager.getState('allCustomers');
        updateStaffView(allCustomers);
    }
}

/**
 * Handle pause toggle
 */
async function handlePauseToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (!currentUser) return;

    const newPauseState = !isSellerPaused;
    await togglePause(currentUser.uid, newPauseState);

    stateManager.setSellerPaused(newPauseState);
    updateShiftAndPauseButtonsState();

    // Refresh view
    const allCustomers = stateManager.getState('allCustomers');
    updateStaffView(allCustomers);
}

/**
 * Update shift and pause button states
 */
export function updateShiftAndPauseButtonsState() {
    const shiftBtn = document.getElementById('shift-btn');
    const pauseBtn = document.getElementById('pause-seller-btn');
    const isShiftActive = stateManager.getState('isShiftActive');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (isShiftActive) {
        // Shift is active - show "End Shift" button
        shiftBtn.textContent = "Terminar Jornada";
        shiftBtn.classList.remove('bg-green-500', 'hover:bg-green-600');
        shiftBtn.classList.add('bg-red-500', 'hover:bg-red-600', 'text-white');
        pauseBtn.style.display = 'flex';

        // Update pause button
        if (isSellerPaused) {
            pauseBtn.title = "Reanudar";
            pauseBtn.classList.replace('bg-yellow-500', 'bg-green-500');
            pauseBtn.innerHTML = `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path></svg>`;
        } else {
            pauseBtn.title = "Pausar";
            pauseBtn.classList.replace('bg-green-500', 'bg-yellow-500');
            pauseBtn.innerHTML = `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>`;
        }
    } else {
        // Shift not active - show "Start Shift" button
        shiftBtn.textContent = "Iniciar Jornada";
        shiftBtn.classList.remove('bg-red-500', 'hover:bg-red-600', 'text-white');
        shiftBtn.classList.add('bg-green-500', 'hover:bg-green-600', 'text-white');
        pauseBtn.style.display = 'none';
    }
}

/**
 * Handle queue actions (call, start attention, finish, etc.)
 */
async function handleQueueAction(e) {
    const target = e.target.closest('button');
    if (!target || !target.dataset.id) return;

    const customerId = target.dataset.id;
    const currentUser = stateManager.getState('currentUser');

    try {
        if (target.classList.contains('call-btn')) {
            await callCustomer(customerId, currentUser);
        } else if (target.classList.contains('start-attention-btn')) {
            await startAttention(customerId, currentUser.email);
        } else if (target.classList.contains('finish-btn')) {
            await finishService(customerId);
        } else if (target.classList.contains('no-show-btn')) {
            await markNoShow(customerId);
        } else if (target.classList.contains('reactivate-btn')) {
            await reactivateCustomer(customerId);
        } else if (target.classList.contains('remove-pref-btn')) {
            await removePreferentialStatus(customerId);
        }
    } catch (error) {
        console.error('Error performing queue action:', error);
        alert('Hubo un error al realizar la acción. Por favor, intenta de nuevo.');
    }
}

/**
 * Call a customer (with seller name lookup)
 */
async function callCustomer(customerId, currentUser) {
    if (!currentUser) return;

    const sellerInfo = await getSellerInfo(currentUser.uid);
    const sellerName = sellerInfo && sellerInfo.name
        ? sellerInfo.name
        : currentUser.email.split('@')[0];

    await callCustomerService(customerId, currentUser.email, sellerName);
}

/**
 * Update staff view with customer list
 *
 * @param {array} customers - All customers
 */
export async function updateStaffView(customers) {
    const allCustomers = customers || stateManager.getState('allCustomers');

    // Update counts
    const counts = getQueueCounts(allCustomers);
    updateQueueCounts(counts);

    // Filter customers based on current filter
    const listFilter = stateManager.getState('listFilter');
    const customersToDisplay = filterCustomers(allCustomers, listFilter);

    // Render customer list
    const queueList = document.getElementById('queue-list');
    queueList.innerHTML = '';

    if (customersToDisplay.length === 0) {
        queueList.innerHTML = `<p class="text-center py-8">No hay clientes en esta vista.</p>`;
        return;
    }

    // Check if current user is busy
    const currentUser = stateManager.getState('currentUser');
    const isCurrentUserBusy = isSellerBusy(currentUser?.email, allCustomers);

    // Create cards for each customer
    for (const customer of customersToDisplay) {
        const customerCard = await createCustomerCard(customer, isCurrentUserBusy);
        queueList.appendChild(customerCard);
    }
}

/**
 * Filter customers based on filter type
 *
 * @param {array} customers - All customers
 * @param {string} filter - Filter type (waiting or standby)
 * @returns {array} Filtered customers
 */
function filterCustomers(customers, filter) {
    if (filter === FILTER_TYPES.WAITING) {
        return customers.filter(c =>
            [STATUS.WAITING, STATUS.CALLING, STATUS.SERVING].includes(c.status)
        );
    } else if (filter === FILTER_TYPES.STANDBY) {
        return customers.filter(c => c.status === STATUS.STANDBY);
    }

    return [];
}

/**
 * Display staff email in header
 *
 * @param {object} user - Current user
 */
export function displayStaffEmail(user) {
    if (user) {
        document.getElementById('staff-email').textContent = `Conectada como: ${user.email}`;
    }
}
